"use client";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/library/Button";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { useFloatingSurfaceVisibility } from "@/hooks/useFloatingSurface";
import { useOrg } from "@/hooks/useOrg";
import {
  buildCannyChangelogAcknowledgement,
  findUserAcknowledgement,
  getUserAcknowledgements,
  setUserAcknowledgement,
  type UserAcknowledgementStatus,
} from "@/utils/acknowledgements/userAcknowledgements";
import {
  CHANGELOG_LAST_SEEN_KEY,
  CHANGELOG_SEEN_ENTRY_IDS_KEY,
  DEFAULT_THRESHOLD_DAYS,
  addSeenChangelogEntryId,
  parseSeenChangelogEntryIds,
  shouldShowChangelogEntry,
  shouldShowEntryForUser,
  truncateText,
} from "@/utils/changelog";
import { canUseCannyFeatures } from "@/utils/changelog/canny";
import { apiLive } from "@/lib/api";
import { BASE_FEEDBACK_URL } from "@/utils/common/baseUrls";

interface ChangelogEntry {
  id: string;
  title: string;
  details: string;
  markdownDetails: string;
  publishedAt: string;
  url: string;
  types: string[];
  labels: string[];
  imageUrl?: string | null;
}

interface ChangelogResponse {
  entries: ChangelogEntry[];
  hasMore: boolean;
}

const readSeenEntryIds = (): string[] => {
  try {
    return parseSeenChangelogEntryIds(
      localStorage.getItem(CHANGELOG_SEEN_ENTRY_IDS_KEY),
      localStorage.getItem(CHANGELOG_LAST_SEEN_KEY)
    );
  } catch {
    return [];
  }
};

const markEntryAsSeen = (entryId: string) => {
  try {
    const seenEntryIds = addSeenChangelogEntryId(readSeenEntryIds(), entryId);

    localStorage.setItem(
      CHANGELOG_SEEN_ENTRY_IDS_KEY,
      JSON.stringify(seenEntryIds)
    );
    localStorage.setItem(CHANGELOG_LAST_SEEN_KEY, entryId);
  } catch {
    // The widget should still dismiss for the current render if storage fails.
  }
};

const persistEntryAcknowledgement = async (
  userId: string,
  entry: ChangelogEntry,
  status: UserAcknowledgementStatus
) => {
  try {
    await setUserAcknowledgement(
      userId,
      buildCannyChangelogAcknowledgement(entry.id),
      status,
      {
        source: "canny_changelog_widget",
        title: entry.title,
        url: entry.url,
      }
    );
  } catch {
    // Local storage still hides the card in the current browser if the server write fails.
  }
};

const ReleaseNotesWidget = () => {
  const { t } = useTranslation("common");
  const { user, isAuthenticated } = useAuthContext();
  const { productAccess } = useOrg();
  const [latestEntry, setLatestEntry] = useState<ChangelogEntry | null>(null);
  const [hasMoreEntries, setHasMoreEntries] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const thresholdDays = DEFAULT_THRESHOLD_DAYS;

  // Fetch changelog entries and check for new ones
  useEffect(() => {
    if (!canUseCannyFeatures() || !isAuthenticated || !user) {
      setLatestEntry(null);
      setIsVisible(false);
      return;
    }

    let isCancelled = false;
    let showTimeout: ReturnType<typeof setTimeout> | null = null;

    const fetchChangelog = async () => {
      try {
        const response = await apiLive<ChangelogResponse>("/api/changelog");

        if (isCancelled) return;

        if (!response?.entries?.length) {
          setLatestEntry(null);
          setIsVisible(false);
          return;
        }

        // Filter entries based on user's product access
        const userAccessibleEntries = response.entries.filter((entry) =>
          shouldShowEntryForUser(
            entry.labels,
            productAccess.userCanUseGailGpt,
            productAccess.userCanUseGailAgent
          )
        );

        if (!userAccessibleEntries.length) {
          setLatestEntry(null);
          setIsVisible(false);
          return;
        }

        const latest = userAccessibleEntries[0];
        const hasMore = response.hasMore || userAccessibleEntries.length > 1;
        const localSeenEntryIds = readSeenEntryIds();
        let seenEntryIds = localSeenEntryIds;

        try {
          const acknowledgements = await getUserAcknowledgements(user.id);
          if (isCancelled) return;

          const serverAcknowledgement = findUserAcknowledgement(
            acknowledgements,
            buildCannyChangelogAcknowledgement(latest.id)
          );

          if (serverAcknowledgement) {
            seenEntryIds = addSeenChangelogEntryId(localSeenEntryIds, latest.id);
            markEntryAsSeen(latest.id);
          }
        } catch {
          // Fall back to local storage when the acknowledgement API is unavailable.
        }

        if (!shouldShowChangelogEntry(latest, seenEntryIds, thresholdDays)) {
          setLatestEntry(null);
          setIsVisible(false);
          return;
        }

        setLatestEntry(latest);
        setHasMoreEntries(hasMore);
        // Small delay before showing to let the page load. Recheck storage so a
        // dismissal in another render/navigation cannot be undone by this timer.
        showTimeout = setTimeout(() => {
          if (
            !isCancelled &&
            shouldShowChangelogEntry(latest, readSeenEntryIds(), thresholdDays)
          ) {
            setIsVisible(true);
          }
        }, 1500);
      } catch (error) {
        // Silently fail - changelog is not critical
        console.info("Failed to fetch changelog:", error);
      }
    };

    fetchChangelog();

    return () => {
      isCancelled = true;

      if (showTimeout) {
        clearTimeout(showTimeout);
      }
    };
  }, [
    isAuthenticated,
    user,
    thresholdDays,
    productAccess.userCanUseGailGpt,
    productAccess.userCanUseGailAgent,
  ]);

  const handleDismiss = useCallback((status: UserAcknowledgementStatus = "dismissed") => {
    setIsVisible(false);
    // Mark as seen so it doesn't show again for this entry
    if (latestEntry && user) {
      markEntryAsSeen(latestEntry.id);
      void persistEntryAcknowledgement(user.id, latestEntry, status);
    }
  }, [latestEntry, user]);

  const handleViewAllUpdates = useCallback(() => {
    if (latestEntry) {
      // Open changelog in new tab
      window.open(`${BASE_FEEDBACK_URL}/changelog`, "_blank");
      handleDismiss("seen");
    }
  }, [latestEntry, handleDismiss]);

  const shouldShow = isAuthenticated && user && latestEntry && isVisible;
  useFloatingSurfaceVisibility("release-notes", Boolean(shouldShow));

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.aside
          initial={{ opacity: 0, y: 24, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          className={clsx("fixed", "z-50", "bottom-6", "left-6")}
          aria-label={t("whatsNew")}
        >
          {/* Stacked cards effect (background cards) */}
          {hasMoreEntries && (
            <>
              <div
                className={clsx(
                  "absolute",
                  "inset-0",
                  "bg-panel",
                  "border",
                  "border-200",
                  "rounded-2xl",
                  "shadow-lg",
                  "-rotate-2",
                  "translate-y-2",
                  "scale-[0.97]"
                )}
              />
              <div
                className={clsx(
                  "absolute",
                  "inset-0",
                  "bg-panel",
                  "border",
                  "border-200",
                  "rounded-2xl",
                  "shadow-lg",
                  "rotate-1",
                  "translate-y-1",
                  "scale-[0.98]"
                )}
              />
            </>
          )}

          {/* Main card */}
          <div
            className={clsx(
              "relative",
              "w-80",
              "max-w-[calc(100vw-3rem)]",
              "bg-panel",
              "border",
              "border-200",
              "rounded-2xl",
              "shadow-2xl",
              "overflow-hidden"
            )}
          >
            {/* Close button */}
            <Button
              onClick={() => handleDismiss("dismissed")}
              type="tertiary"
              iconOnly
              iconBefore={<Icon icon="xmark" size={ICON_SIZES.SM} />}
              className="top-3 right-3 z-10 absolute"
            />

            {/* Content */}
            <div className={clsx("p-5", "pb-4")}>
              <h3
                id="release-notes-title"
                className={clsx(
                  "text-lg",
                  "font-bold",
                  "text-900",
                  "pr-8",
                  "mb-1.5",
                  "line-clamp-2"
                )}
              >
                {truncateText(latestEntry.title, 50)}
              </h3>
              <p
                className={clsx(
                  "text-sm",
                  "text-500",
                  "leading-relaxed",
                  "line-clamp-2"
                )}
              >
                {truncateText(latestEntry.details, 80)}
              </p>
            </div>

            {/* Image */}
            {latestEntry.imageUrl && (
              <div
                className={clsx(
                  "relative",
                  "mx-5",
                  "mb-4",
                  "h-40",
                  "rounded-xl",
                  "overflow-hidden",
                  "bg-100",
                  "border",
                  "border-200"
                )}
              >
                <Image
                  src={latestEntry.imageUrl}
                  alt=""
                  fill
                  // and cen
                  className={clsx("object-contain", "bg-panel")}
                  unoptimized
                />
              </div>
            )}

            {/* View all updates button */}
            <div className={clsx("px-5", "pb-5")}>
              <Button onClick={handleViewAllUpdates} wide>
                {t("viewAllUpdates")}
              </Button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};

export default ReleaseNotesWidget;
